import React, {useEffect, useState} from 'react';
import {StyleSheet} from 'react-native';
import {Heading, Box, Center, VStack} from 'native-base';
import {KeyboardAwareScrollView} from 'react-native-keyboard-aware-scroll-view';
import {
  useForm,
  SubmitHandler,
  SubmitErrorHandler,
  FieldErrors,
} from 'react-hook-form';
import {
  MaskedInput,
  DropDownInput,
  ReadonlyInput,
  ReadonlyTextArea,
} from '../../../../../components/inputs';
import {
  PrimaryButton,
  SecondaryButton,
} from '../../../../../components/buttons';
import IDropDownOption from '../../../../../interfaces/ui/dropdown-option.interface';
import useToast from '../../../../../hooks';
import {message as t} from '../../../../../utils/messages';
import {useContextState} from '../../../../../context/sub-client';
import {isWebApp} from '../../../../../utils/platform/is-web';
import {NativeStackScreenProps} from '@react-navigation/native-stack';
import {StackParamList} from '../../../../../navigators';
import InternalTemplate from '../../../../../components/templates/internal';
import {IC2PPaymentForm} from '../../../../../interfaces/forms/c2p-payment-form.interface';
import {SpinnerModal} from '../../../../../components/modals';
import {formatFeeQuote, formatNumber} from '../../../../../utils/format';

type ScreenProps = NativeStackScreenProps<
  StackParamList,
  'PaymentC2pFormScreen'
>;

const PaymentC2pFormScreen = ({navigation}: ScreenProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const {
    basePaymentC2P,
    fetchBasePaymentC2P,
    invoiceFeeSummary,
    sendPaymentC2P,
  } = useContextState();
  const {showInputsError, showC2PError} = useToast();

  const isEditableAmount = () => {
    if (basePaymentC2P) {
      return basePaymentC2P.amountEditable;
    }
    return true;
  };

  const calculateAmount = () => {
    let defaultAmount = 0;

    invoiceFeeSummary?.fees.forEach(fee => {
      defaultAmount += fee.amount.local;
    });

    return defaultAmount;
  };

  const getAmountError = () => {
    return `${t('screen-c2p-payment-total-amount-error')} ${formatNumber(
      calculateAmount(),
    )}`;
  };

  const {
    control,
    handleSubmit,
    formState: {errors},
    reset,
  } = useForm({
    defaultValues: {
      bank: undefined,
      documentType: undefined,
      documentNumber: undefined,
      phonePrefix: undefined,
      phoneNumber: undefined,
      amount: calculateAmount(),
    } as IC2PPaymentForm,
  });

  const loadBasePaymentC2P = async () => {
    setIsLoading(true);
    await fetchBasePaymentC2P();
    setIsLoading(false);
  };

  useEffect(() => {
    if (basePaymentC2P == null) {
      loadBasePaymentC2P();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onInvalid: SubmitErrorHandler<IC2PPaymentForm> = async (
    fieldErrors: FieldErrors<IC2PPaymentForm>,
  ) => {
    if (fieldErrors) {
      showInputsError();
    }
  };

  const getBankList = (): IDropDownOption[] => {
    if (basePaymentC2P?.bankList) {
      return basePaymentC2P?.bankList.map(item => ({
        label: item.title,
        value: item.id,
      }));
    }
    return [];
  };

  const getDocumentTypeList = (): IDropDownOption[] => {
    if (basePaymentC2P?.documentTypeList) {
      return basePaymentC2P?.documentTypeList.map(item => ({
        label: item.title,
        value: item.id,
      }));
    }
    return [];
  };

  const getFeeListInputValue = () => {
    if (
      invoiceFeeSummary &&
      invoiceFeeSummary.fees &&
      invoiceFeeSummary.fees.length > 0
    ) {
      return invoiceFeeSummary.fees
        .map(fee => formatFeeQuote(fee.dueDate.day, fee.dueDate.month))
        .join(', ');
    }
    return '';
  };

  const getFeeList = () => {
    if (
      invoiceFeeSummary &&
      invoiceFeeSummary.fees &&
      invoiceFeeSummary.fees.length > 0
    ) {
      return invoiceFeeSummary.fees.map(fee => fee.id);
    }
    return [];
  };

  const getPhonePrefixList = (): IDropDownOption[] => {
    if (basePaymentC2P?.phonePrefixList) {
      return basePaymentC2P?.phonePrefixList.map(item => ({
        label: item.title,
        value: item.id,
      }));
    }
    return [];
  };

  const handleClearForm = () => {
    reset();
  };

  const handleGoBack = () => {
    navigation.goBack();
  };

  const onSubmit: SubmitHandler<IC2PPaymentForm> = async (
    data: IC2PPaymentForm,
  ) => {
    try {
      setIsLoading(true);
      const result = await sendPaymentC2P({
        ...data,
        invoiceId: invoiceFeeSummary?.id,
        invoiceFees: getFeeList(),
      } as IC2PPaymentForm);
      if (result) {
        navigation.navigate('PaymentC2pConfirmationScreen');
      }
      setIsLoading(false);
    } catch {
      setIsLoading(false);
      showC2PError();
    }
    return false;
  };

  return (
    <InternalTemplate
      title={'screen-c2p-payment-title'}
      contentPaddingX="0"
      backButton={{
        onPress: () => handleGoBack(),
        label: 'back-dashboard',
        usage: 'external',
      }}>
      <VStack space={3} justifyContent="center" marginX={4}>
        <SpinnerModal
          show={isLoading}
          title={t('screen-c2p-payment-send-pay')}
        />
        <Box flex={1}>
          <KeyboardAwareScrollView
            style={styles.content}
            enableOnAndroid={true}
            scrollEnabled={true}
            enableAutomaticScroll={true}
            showsVerticalScrollIndicator={isWebApp}
            extraHeight={100}
            extraScrollHeight={100}
            innerRef={ref => {
              (this as any).scroll = ref;
            }}>
            <Center width="100%" flex={1} justifyContent="flex-start">
              <Box width="100%" py={5}>
                <Heading
                  size="xs"
                  paddingBottom="5"
                  fontWeight="bold"
                  color="#06355E">
                  {t('screen-c2p-payment-label')}
                </Heading>
                <DropDownInput
                  control={control}
                  name="bank"
                  label="screen-c2p-payment-bank-label"
                  options={getBankList()}
                  error={errors.bank}
                  rules={{
                    required: 'form-is-required',
                  }}
                />
                <DropDownInput
                  control={control}
                  name="documentType"
                  label="screen-c2p-payment-document-type-label"
                  options={getDocumentTypeList()}
                  error={errors.documentType}
                  rules={{
                    required: 'form-is-required',
                  }}
                />
                <MaskedInput
                  control={control}
                  name="documentNumber"
                  label="screen-c2p-payment-document-number-label"
                  mask="document-number"
                  error={errors.documentNumber}
                  rules={{
                    required: 'form-is-required',
                  }}
                />
                <DropDownInput
                  control={control}
                  name="phonePrefix"
                  label="screen-c2p-payment-phone-prefix-label"
                  options={getPhonePrefixList()}
                  error={errors.phonePrefix}
                  rules={{
                    required: 'form-is-required',
                  }}
                />
                <MaskedInput
                  control={control}
                  name="phoneNumber"
                  label="screen-c2p-payment-phone-number-label"
                  mask="phone-base"
                  error={errors.phoneNumber}
                  rules={{
                    required: 'form-is-required',
                  }}
                />
                <MaskedInput
                  control={control}
                  name="amount"
                  label="screen-c2p-payment-total-amount-label"
                  mask="money"
                  error={errors.amount}
                  rules={{
                    required: 'form-is-required',
                    max: {
                      message: getAmountError(),
                      value: calculateAmount(),
                    },
                  }}
                  isEditable={isEditableAmount()}
                />
                <ReadonlyInput
                  label="screen-c2p-payment-invoice-number-label"
                  value={`${invoiceFeeSummary?.id}`}
                  backgroundColor="#F8F8F8"
                />

                <ReadonlyTextArea
                  label="screen-c2p-payment-invoice-fees-label"
                  value={getFeeListInputValue()}
                  numberOfLines={5}
                  maxLength={250}
                  backgroundColor="#F8F8F8"
                />

                <Box mt={'5'}>
                  <PrimaryButton
                    label="screen-c2p-payment-send"
                    onPress={handleSubmit(onSubmit, onInvalid)}
                  />
                </Box>
                <Box mt={5} mb={200}>
                  <SecondaryButton
                    label="screen-c2p-payment-button-cancel"
                    onPress={handleClearForm}
                  />
                </Box>
              </Box>
            </Center>
          </KeyboardAwareScrollView>
        </Box>
      </VStack>
    </InternalTemplate>
  );
};

const styles = StyleSheet.create({
  content: {
    flex: 1,
  },
  file: {
    marginTop: 15,
  },
});

export {PaymentC2pFormScreen};
